import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import HelpModal from '../HelpModal';
import { FaPlay, FaPause, FaVolumeUp, FaVolumeMute } from 'react-icons/fa';
import HEXASLogo from '../assets/Logos/HEXASLogo.jpg';
import { useTextHighlight } from '../utils/highlightUtils';

const Class11NotePractice02 = () => {
  const navigate = useNavigate();

  const highlightRef = useRef(null);
  useTextHighlight(highlightRef);

  const [showHelp, setShowHelp] = useState(false);

  const questionRefs = useRef([]);
  const rightColRef = useRef(null);

  // Make sure your answers state exists
  const [answers, setAnswers] = useState(Array(20).fill(''));
  const [currentQuestion, setCurrentQuestion] = useState(14);
  const onNext = () => {
    setCurrentQuestion(prev => Math.min(prev + 1, 19));
  };

  const audioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);

  // Timer state
  const [timeRemaining, setTimeRemaining] = useState(100 * 60); // 30 minutes in seconds
  const [showSeconds, setShowSeconds] = useState(false);

  // Timer effect
  useEffect(() => {
    const timer = setInterval(() => {
      setTimeRemaining(prev => {
        if (prev <= 0) {
          clearInterval(timer);
          navigate('/');  // Redirect to home
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [navigate]);

  // Format time function
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;

    if (showSeconds) {
      return `${mins} : ${secs.toString().padStart(2, '0')} remaining`;
    }
    return `${mins} minutes remaining`;
  };

  const handleStartTest = () => {
    navigate(`/guided-practice-01-form`);
  };

  const handlePlayPause = () => {
    if (!audioRef.current) return;
    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play();
    }
    setIsPlaying(!isPlaying);
  };

  const handleTimeUpdate = () => {
    if (!audioRef.current) return;
    setCurrentTime(audioRef.current.currentTime);
  };

  const handleLoadedMetadata = () => {
    if (!audioRef.current) return;
    setDuration(audioRef.current.duration);
  };

  const handleSeek = (e) => {
    if (!audioRef.current) return;
    const time = parseFloat(e.target.value);
    audioRef.current.currentTime = time;
    setCurrentTime(time);
  };

  const handleVolumeChange = (e) => {
    if (!audioRef.current) return;
    const vol = parseFloat(e.target.value);
    setVolume(vol);
    audioRef.current.volume = vol;
    setIsMuted(vol === 0);
  };

  const handleMute = () => {
    if (!audioRef.current) return;
    if (isMuted) {
      audioRef.current.muted = false;
      setIsMuted(false);
      audioRef.current.volume = volume;
    } else {
      audioRef.current.muted = true;
      setIsMuted(true);
    }
  };

  const formatAudioTime = (t) => {
    if (isNaN(t)) return '00:00';
    const m = Math.floor(t / 60);
    const s = Math.floor(t % 60);
    return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  };

  const GreenCheck = () => (
    <svg
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className="inline-block"
    >
      <path
        d="M7 11L9 13.5L16 7"
        stroke="#7DD77D"
        strokeWidth="3"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );


  return (
    <div className="min-h-screen bg-white flex flex-col font-[Arial]">
      {/* Help Modal */}
      <HelpModal
        isOpen={showHelp}
        onClose={() => setShowHelp(false)}
      />

      {/* Top Bar (fixed) */}
      <div className="sticky top-0 z-30 bg-white">
        <div className="relative h-[62px] bg-[#F4C3C3] border-b border-black flex items-center justify-between px-4">
          {/* Left + Center combined on left side */}
          <div className="flex flex-col justify-center space-y-0.5 pl-6">
            <span className="text-[14px] text-left text-black font-bold leading-tight">
              HEXA'S Education
            </span>
            <div className="flex items-center space-x-6 text-[14px] text-black font-medium">
              <span
                onMouseEnter={() => setShowSeconds(true)}
                onMouseLeave={() => setShowSeconds(false)}
                className="cursor-pointer"
              >
                {formatTime(timeRemaining)}
              </span>
            </div>
          </div>

          {/* Right: Icons and Finish Button */}
          <div className="flex items-center space-x-5">
            <button
              onClick={() => navigate('/')}
              className="text-black border border-black rounded-md px-4 py-1 text-[14px]  hover:bg-gray-100"
            >
              Back Home
            </button>
            <button
              onClick={() => setShowHelp(true)}
              className="text-black border border-black rounded-md px-4 py-1 text-[14px]  hover:bg-gray-100"
            >
              Finish test
            </button>

            {/* Icons container with larger size and spacing */}
            <div className="flex items-center space-x-8 pl-2 pr-2">
              {/* WiFi Icon */}
              <div className="w-10 h-10 flex items-center justify-center">
                <svg className="w-9 h-9 text-black" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M17.778 8.222c-4.296-4.296-11.26-4.296-15.556 0A1 1 0 010.808 6.808c5.076-5.077 13.308-5.077 18.384 0a1 1 0 01-1.414 1.414zM14.95 11.05a7 7 0 00-9.9 0 1 1 0 01-1.414-1.414 9 9 0 0112.728 0 1 1 0 01-1.414 1.414zM12.12 13.88a3 3 0 00-4.24 0 1 1 0 01-1.415-1.415 5 5 0 017.07 0 1 1 0 01-1.415 1.415zM9 16a1 1 0 011-1h.01a1 1 0 110 2H10a1 1 0 01-1-1z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>

              {/* Notification Bell Icon */}
              <div className="w-10 h-10 flex items-center justify-center">
                <svg className="w-8 h-8 text-black" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M10 2a6 6 0 00-6 6v3.586l-.707.707A1 1 0 004 14h12a1 1 0 00.707-1.707L16 11.586V8a6 6 0 00-6-6zM10 18a3 3 0 01-3-3h6a3 3 0 01-3 3z" />
                </svg>
              </div>

              {/* Hamburger Menu Icon */}
              <div className="w-10 h-10 flex items-center justify-center">
                <svg className="w-8 h-8 text-black" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M3 5a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM3 10a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM3 15a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>

              {/* Pen/Edit Icon */}
              <div className="w-10 h-10 flex items-center justify-center">
                <svg className="w-8 h-8 text-black" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                </svg>
              </div>
            </div>
          </div>
        </div>
        {/* Title Bar (fixed under top bar) */}
        <div className="relative bg-[#F4F7FA] shadow-sm border border-black rounded-lg mx-12 mt-4 mb-0 sticky top-[32px] z-20 flex items-center justify-between">
          {/* Left: Part Title */}
          <div className="flex-1 px-4 py-2 text-left">
            <h2 className="text-[23px] text-black">Part 1</h2>
            <h1 className="text-[18px] text-black">HEXA'S Reading Course Book</h1>
          </div>
          {/* Center: Absolutely Positioned Logo */}
          <div className="absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2">
            <img
              src={HEXASLogo}
              alt="HEXA'S Logo"
              className="h-[50px] object-contain"
            />
          </div>
          {/* Right: Empty space for consistency */}
          <div className="flex items-center justify-end w-[300px] pr-4">
            {/* Empty space - no audio player for reading */}
          </div>
        </div>
      </div>

      {/* Main content (scrollable) */}
      <div className="flex-1 flex flex-col items-center justify-start w-full">
        {/* Two-Column Main Content Area */}
        <div ref={highlightRef}
          className="flex flex-row w-full overflow-hidden -mb-16 mt-2 px-12 pb-20"
          style={{ height: 'calc(100vh - 180px)' }}
        >
          {/* Left: Reading Passage */}
          <div className="bg-[#F4F7FA] rounded-md shadow border border-gray-300 p-6 w-[49%] h-full max-h-full overflow-y-auto">
            <div className="mb-8">
              <h2 className="text-[16px] font-bold mb-4 text-center">Stonehenge</h2>
              <h3 className="text-[14px] font-semibold mb-6 text-center italic">
                For centuries, historians and archaeologists have puzzled over the many mysteries of Stonehenge, a prehistoric monument that took an estimated 1,500 years to erect.
              </h3>

              {/* Remaining paragraphs - left aligned */}
              <div className="text-[14px] space-y-4 text-left">
                <p>
                  Located on Salisbury Plain in southern England, it is comprised of roughly 100 massive upright stones placed in a circular layout.
                </p>
                <p>
                  Archaeologists believe England’s most iconic prehistoric ruin was built in several stages with the earliest constructed 5,000 or more years ago. First, Neolithic* Britons used primitive tools, which may have been fashioned out of deer antlers, to dig a massive circular ditch and bank, or henge. Deep pits dating back to that era and located within the circle may have once held a ring of timber posts, according to some scholars.
                </p>
                <p>
                  Several hundred years later, it is thought, Stonehenge’s builders hoisted an estimated 80 bluestones, 43 of which remain today, into standing positions and placed them in either a horseshoe or circular formation. These stones have been traced all the way to the Preseli Hills in Wales, some 300 kilometres from Stonehenge. How, then, did prehistoric builders without sophisticated tools or engineering haul these boulders, which weigh up to four tons, over such a great distance?
                </p>
                <p>
                  According to one long-standing theory among archaeologists, Stonehenge’s builders fashioned sledges and rollers out of tree trunks to lug the bluestones from the Preseli Hills. They then transferred the boulders onto rafts and floated them first along the Welsh coast and then up the River Avon toward Salisbury Plain; alternatively, they may have towed each stone with a fleet of vessels. More recent archaeological hypotheses have them transporting the bluestones with supersized wicker baskets on a combination of ball bearings and long grooved planks, hauled by oxen.
                </p>
                <p>
                  As early as the 1970s, geologists have been adding their voices to the debate over how Stonehenge came into being. Challenging the classic image of industrious builders pushing, carting, rolling or hauling giant stones from faraway Wales, some scientists have suggested that it was glaciers, not humans, that carried the bluestones to Salisbury Plain. Most archaeologists have remained sceptical about this theory, however, wondering how the forces of nature could possibly have delivered the exact number of stones needed to complete the circle.
                </p>
                <p>
                  The third phase of construction took place around 2000 BCE. At this point, sandstone slabs – known as ‘sarsens’ – were arranged into an outer crescent or ring; some were assembled into the iconic three-pieced structures called trilithons that stand tall in the centre of Stonehenge. Some 50 of these stones are now visible on the site, which may once have contained many more. Radiocarbon dating has revealed that work continued at Stonehenge until roughly 1600 BCE, with the bluestones in particular being repositioned multiple times.
                </p>
                <p>
                  But who were the builders of Stonehenge? In the 17th century, archaeologist John Aubrey made the claim that Stonehenge was the work of druids, who had important religious, judicial and political roles in Celtic** society. This theory was widely popularized by the antiquarian William Stukeley, who had unearthed primitive graves at the site. Even today, people who identify as modern druids continue to gather at Stonehenge for the summer solstice. However, in the mid-20th century, radiocarbon dating demonstrated that Stonehenge stood more than 1,000 years before the Celts inhabited the region.
                </p>
                <p>
                  Many modern historians and archaeologists now agree that several distinct tribes of people contributed to Stonehenge, each undertaking a different phase of its construction. Bones, tools and other artefacts found on the site seem to support this hypothesis. The first stage was achieved by Neolithic agrarians who were likely to have been indigenous to the British Isles. Later, it is believed, groups with advanced tools and a more communal way of life left their mark on the site. Some believe that they were immigrants from the European continent, while others maintain that they were probably native Britons, descended from the original builders.
                </p>
                <p>
                  If the facts surrounding the architects and construction of Stonehenge remain shadowy at best, the purpose of the striking monument is even more of a mystery. While there is consensus among the majority of modern scholars that Stonehenge once served the function of burial ground, they have yet to determine what other purposes it had.
                </p>
                <p>
                  In the 1960s, the astronomer Gerald Hawkins suggested that the cluster of megalithic stones operated as a form of calendar, with different points corresponding to astrological phenomena such as solstices, equinoxes and eclipses occurring at different times of the year. While his theory has received a considerable amount of attention over the decades, critics maintain that Stonehenge’s builders probably lacked the knowledge necessary to predict such events or that England’s dense cloud cover would have obscured their view of the skies.
                </p>
                <p>
                  More recently, signs of illness and injury in the human remains unearthed at Stonehenge led a group of British archaeologists to speculate that it was considered a place of healing, perhaps because bluestones were thought to have curative powers.
                </p>
              </div>
            </div>
          </div>





          {/* Right: Questions */}
          <div
            className="w-[50%] flex flex-col items-start bg-[#F4F7FA] rounded-md shadow border border-gray-300 p-6 h-full max-h-full overflow-y-auto relative"
            style={{ overflowAnchor: 'none' }}
          >
            {/* Questions 1 – 8: Notes */}
            <div className="w-full">
              <h3 className="text-[15px] font-bold mb-3 text-left">Questions 1 – 8</h3>
              <p className="text-[14px] mb-2 text-left italic">
                Complete the notes below.
              </p>
              <p className="text-[14px] mb-4 text-left">
                Choose <strong>NO MORE THAN TWO WORDS</strong> from the passage for each answer.
              </p>

              <h2 className="text-[16px] font-bold text-center mb-2">Stonehenge</h2>

              <h3 className="text-[15px] font-bold mb-1 mt-4 text-left">Construction</h3>
              <div className="text-[14px] font-medium text-blac text-leftk">
                <div className="ml-2 mb-2 text-left">
                  <span className="font-semibold text-left">Stage 1:</span>
                  <ul className="list-disc list-inside ml-5 mt-1">
                    <li>
                      the ditch and henge were dug, possibly using tools made from&nbsp;
                      <input
                        ref={(el) => (questionRefs.current[0] = el)}
                        type="text"
                        placeholder="1"
                        className="inline-block align-middle rounded bg-[#E0F2FE] border border-[#7DD3FC] px-2 py-1 shadow w-[110px] max-w-full text-[13px] font-semibold outline-none placeholder:text-black focus:placeholder:opacity-0"
                        value={answers[0] || ""}
                        onChange={(e) => {
                          const newAnswers = [...answers];
                          newAnswers[0] = e.target.value;
                          setAnswers(newAnswers);
                        }}
                      />
                      .
                    </li>
                    <li>
                      <input
                        ref={(el) => (questionRefs.current[1] = el)}
                        type="text"
                        placeholder="2"
                        className="inline-block align-middle rounded bg-[#E0F2FE] border border-[#7DD3FC] px-2 py-1 shadow w-[110px] max-w-full text-[13px] font-semibold outline-none placeholder:text-black focus:placeholder:opacity-0"
                        value={answers[1] || ""}
                        onChange={(e) => {
                          const newAnswers = [...answers];
                          newAnswers[1] = e.target.value;
                          setAnswers(newAnswers);
                        }}
                      />
                      &nbsp;may have been arranged in deep pits inside the circle
                    </li>
                  </ul>
                </div>
                <div className="ml-2 mb-2 text-left">
                  <span className="font-semibold text-left">Stage 2:</span>
                  <ul className="list-disc list-inside ml-5 mt-1">
                    <li>bluestones from the Preseli Hills were placed in standing position</li>
                    <li>
                      theories about the transportation of the bluestones:
                      <ul className="list-none ml-5 mt-1">
                        <li>
                          <span className="font-semibold">archaeological:</span>
                          <ul className="list-disc list-inside ml-5 mt-1">
                            <li>
                              builders used&nbsp;
                              <input
                                ref={(el) => (questionRefs.current[2] = el)}
                                type="text"
                                placeholder="3"
                                className="inline-block align-middle rounded bg-[#E0F2FE] border border-[#7DD3FC] px-2 py-1 shadow w-[110px] max-w-full text-[13px] font-semibold outline-none placeholder:text-black focus:placeholder:opacity-0"
                                value={answers[2] || ""}
                                onChange={(e) => {
                                  const newAnswers = [...answers];
                                  newAnswers[2] = e.target.value;
                                  setAnswers(newAnswers);
                                }}
                              />
                              &nbsp;to make sledges and rollers
                            </li>
                            <li>
                              <input
                                ref={(el) => (questionRefs.current[3] = el)}
                                type="text"
                                placeholder="4"
                                className="inline-block align-middle rounded bg-[#E0F2FE] border border-[#7DD3FC] px-2 py-1 shadow w-[110px] max-w-full text-[13px] font-semibold outline-none placeholder:text-black focus:placeholder:opacity-0"
                                value={answers[3] || ""}
                                onChange={(e) => {
                                  const newAnswers = [...answers];
                                  newAnswers[3] = e.target.value;
                                  setAnswers(newAnswers);
                                }}
                              />
                              &nbsp;pulled them on giant baskets
                            </li>
                          </ul>
                        </li>
                        <li>
                          <span className="font-semibold">geological:</span>
                          <ul className="list-disc list-inside ml-5 mt-1">
                            <li>
                              they were brought from Wales by&nbsp;
                              <input
                                ref={(el) => (questionRefs.current[4] = el)}
                                type="text"
                                placeholder="5"
                                className="inline-block align-middle rounded bg-[#E0F2FE] border border-[#7DD3FC] px-2 py-1 shadow w-[110px] max-w-full text-[13px] font-semibold outline-none placeholder:text-black focus:placeholder:opacity-0"
                                value={answers[4] || ""}
                                onChange={(e) => {
                                  const newAnswers = [...answers];
                                  newAnswers[4] = e.target.value;
                                  setAnswers(newAnswers);
                                }}
                              />
                              .
                            </li>
                          </ul>
                        </li>
                      </ul>
                    </li>
                  </ul>
                </div>
                <div className="ml-2 mb-2 text-left">
                  <span className="font-semibold text-left">Stage 3:</span>
                  <ul className="list-disc list-inside ml-5 mt-1">
                    <li>sandstone slabs were arranged into an outer crescent or ring</li>
                  </ul>
                </div>
                <h3 className="text-[15px] font-bold mb-1 mt-4 text-left">Builders</h3>
                <ul className="list-disc list-inside ml-5 mt-1">
                  <li>
                    a theory arose in the 17th century that its builders were Celtic&nbsp;
                    <input
                      ref={(el) => (questionRefs.current[5] = el)}
                      type="text"
                      placeholder="6"
                      className="inline-block align-middle rounded bg-[#E0F2FE] border border-[#7DD3FC] px-2 py-1 shadow w-[110px] max-w-full text-[13px] font-semibold outline-none placeholder:text-black focus:placeholder:opacity-0"
                      value={answers[5] || ""}
                      onChange={(e) => {
                        const newAnswers = [...answers];
                        newAnswers[5] = e.target.value;
                        setAnswers(newAnswers);
                      }}
                    />
                    .
                  </li>
                </ul>
                <h3 className="text-[15px] font-bold mb-1 mt-4 text-left">Purpose</h3>
                <ul className="list-disc list-inside ml-5 mt-1">
                  <li>
                    many experts agree it has been used as a&nbsp;
                    <input
                      ref={(el) => (questionRefs.current[6] = el)}
                      type="text"
                      placeholder="7"
                      className="inline-block align-middle rounded bg-[#E0F2FE] border border-[#7DD3FC] px-2 py-1 shadow w-[110px] max-w-full text-[13px] font-semibold outline-none placeholder:text-black focus:placeholder:opacity-0"
                      value={answers[6] || ""}
                      onChange={(e) => {
                        const newAnswers = [...answers];
                        newAnswers[6] = e.target.value;
                        setAnswers(newAnswers);
                      }}
                    />
                    &nbsp;site
                  </li>
                  <li>
                    in the 1960s, it was suggested that it worked as a kind of&nbsp;
                    <input
                      ref={(el) => (questionRefs.current[7] = el)}
                      type="text"
                      placeholder="8"
                      className="inline-block align-middle rounded bg-[#E0F2FE] border border-[#7DD3FC] px-2 py-1 shadow w-[110px] max-w-full text-[13px] font-semibold outline-none placeholder:text-black focus:placeholder:opacity-0"
                      value={answers[7] || ""}
                      onChange={(e) => {
                        const newAnswers = [...answers];
                        newAnswers[7] = e.target.value;
                        setAnswers(newAnswers);
                      }}
                    />
                    .
                  </li>
                </ul>
              </div>
            </div>
          </div>








        </div>
      </div>

      {/* Bottom Navigation (fixed footer) */}
      <div className="sticky bottom-0 left-0 w-full bg-white border-t border-gray-300 z-30 flex items-center justify-start px-4 py-3">
        <div className="flex items-center max-w-3xl space-x-2 overflow-x-auto no-scrollbar">
          {/* Part Label with bar above */}
          <div className="flex flex-col items-center mr-4 flex-shrink-0">
            <div className="h-1 w-12 mb-1 rounded-full bg-gray-300" />
            <span className="text-[15px] font-bold text-gray-700">Part 1</span>
          </div>

          {[...Array(8).keys()].map((i) => {
            const num = i + 1;
            const isActive = currentQuestion === num;
            const isAnswered = answers[num - 1] && answers[num - 1] !== '';
            return (
              <div key={num} className="flex flex-col items-center flex-shrink-0">
                <div
                  className="h-1 w-7 mb-1 rounded-full"
                  style={{ backgroundColor: isAnswered ? '#C9A842' : '#D1D5DB' }}
                ></div>
                <button
                  onClick={() => {
                    setCurrentQuestion(num);
                    setTimeout(() => {
                      const input = questionRefs.current[num - 1];
                      if (input) {
                        input.scrollIntoView({ behavior: 'smooth', block: 'center' });
                        input.focus();
                      }
                    }, 50);
                  }}
                  className={`w-7 h-7 flex items-center justify-center text-[15px] font-bold
              ${isActive ? 'border-2 border-sky-400 bg-white text-sky-700 rounded-md' : 'text-gray-700'}
              transition-colors duration-150`}
                  style={{ boxShadow: isActive ? '0 0 0 0px #38bdf8 inset' : 'none' }}
                >
                  {num}
                </button>
              </div>
            );
          })}
        </div>
      </div>







    </div>
  );
};

export default Class11NotePractice02;
